/**
 * TableRenderer - Renders data tables
 */

import React, { useState, useMemo } from 'react'

interface TableRendererProps {
  data: any
  options?: Record<string, unknown>
  compact?: boolean
}

function TableRenderer({ data, options, compact }: TableRendererProps) {
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const pageSize = compact ? 5 : 10

  // Extract columns and rows from data
  const { columns, rows } = useMemo(() => {
    if (!data) return { columns: [], rows: [] }
    
    // If data has explicit columns/rows structure
    if (data.columns && data.rows) {
      return {
        columns: data.columns,
        rows: data.rows,
      }
    }
    
    // If data is an array, infer columns from first row
    if (Array.isArray(data) && data.length > 0) {
      const firstRow = data[0]
      const cols = Object.keys(firstRow).map(key => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        type: typeof firstRow[key] === 'number' ? 'number' : 'string',
      }))
      return {
        columns: cols,
        rows: data,
      }
    }
    
    return { columns: [], rows: [] }
  }, [data])

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows
    
    return [...rows].sort((a, b) => {
      const aVal = a[sortColumn]
      const bVal = b[sortColumn]
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      
      const aStr = String(aVal || '')
      const bStr = String(bVal || '')
      return sortDirection === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr)
    })
  }, [rows, sortColumn, sortDirection])

  // Paginate
  const paginatedRows = sortedRows.slice(page * pageSize, (page + 1) * pageSize)
  const totalPages = Math.ceil(sortedRows.length / pageSize)

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(columnKey)
      setSortDirection('asc')
    }
  }

  if (columns.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        No data available
      </div>
    )
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800">
              {columns.map((col: any) => (
                <th
                  key={col.key}
                  onClick={() => handleSort(col.key)}
                  className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-1">
                    {col.label}
                    {sortColumn === col.key && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={sortDirection === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                        />
                      </svg>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedRows.map((row: any, rowIndex: number) => (
              <tr
                key={rowIndex}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                {columns.map((col: any) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-gray-800 dark:text-gray-200 ${
                      col.type === 'number' ? 'text-right font-mono' : ''
                    }`}
                  >
                    {formatCellValue(row[col.key], col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, sortedRows.length)} of {sortedRows.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 text-sm border border-gray-200 dark:border-gray-700 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function formatCellValue(value: any, col: any): string {
  if (value === null || value === undefined) return '—'
  
  if (col.type === 'number' && typeof value === 'number') {
    if (col.format === 'currency') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
    }
    if (col.format === 'percentage') {
      return `${value.toFixed(1)}%`
    }
    return value.toLocaleString()
  }
  
  if (col.type === 'date' && value) {
    try {
      return new Date(value).toLocaleDateString()
    } catch {
      return String(value)
    }
  }
  
  return String(value)
}

export default TableRenderer



